import { Component, OnInit, Inject, OnDestroy, Input, ViewChild } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { ValidatorFn, AbstractControl, UntypedFormGroup, UntypedFormBuilder, FormControl, Validators } from '@angular/forms';
import { EcmToolChangeService } from '../ecm-tool-change.service';
import { CgpAlertDialogService } from 'src/app/shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { cgpAlertTypes } from 'src/app/shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { Subscription } from 'rxjs';

// ECM Tool Change (ETC) Multi Row Update
@Component({
   // tslint:disable-next-line: component-selector
   selector: 'etc-multi-row-update',
   templateUrl: './etc-multi-row-update.component.html',
   styleUrls: ['./etc-multi-row-update.component.scss']
})
export class EtcMultiRowUpdateComponent implements OnInit, OnDestroy {

   readonly NEC_LEC_MIN_LENGTH = 1;
   readonly NEC_LEC_MAX_LENGTH = 7;
   readonly NEC_LEC_REGEX = /^(REL|\(REL\)|\(REL|REL\)|[A-Z][A-Z]?)$/i;

   multiRowUpdateForm: UntypedFormGroup;
   designSources: any[];
   loading = false;
   private readonly _subs: Subscription[] = [];
   isAllNecLecSame = false;
   isAllToolAffectedSame = false;
   isAllCertDataAffectedSame = false;
   isAllTlLeadRelLvlSame = false;
   isAllDtShippedToDatabankSame = false;
   isAllNecCompleteDateSame = false;
   isAllDesignSourceIdSame = false;
   selectionsHasNoDtShippedToDatabank = false;
   selectionsHasAllDtShippedToDatabank = false;
   multiSelect = false;
   formFieldDisabled: boolean;
   selectionsHasAllTlLeadRelLvl = false;
   selectionsHasAllDesignSourceId = false;
   toolLdReleaseLvlFlag = false;
   selectionsHasAllNecLec = false;
   checkCondition = false;
   selectionsHasOneDtShippedToDatabank = false;

   constructor(
      private readonly dialogRef: MatDialogRef<EtcMultiRowUpdateComponent>,
      private readonly fb: UntypedFormBuilder,
      private readonly ecmToolChangeService: EcmToolChangeService,
      private readonly cgpAlertDialogService: CgpAlertDialogService,
      @Inject(MAT_DIALOG_DATA) private readonly data: any
   ) {
      this.multiRowUpdateForm = this.fb.group({
         chkNecLec: [false],
         chkToolAffected: [false],
         chkCertDataAffected: [false],
         chkTlLeadRelLvl: [false],
         chkDtShippedToDataBank: [false],
         chkNecCompleteDate: [false],
         chkDesignSourceId: [false],
         designSourceId: [],
         dtShippedToDatabank: [],
         necCompleteDate: [],
         necLec: ['', [Validators.pattern(this.NEC_LEC_REGEX)]],
         tlLeadRelLvl: [''],
         certDataAffected: [false],
         toolAffected: [false]
      });
   }

   ngOnInit() {
      this.designSources = this.data.designSources;
      const selections = this.data.selections;
      const ctrl = this.multiRowUpdateForm.controls;
      if (selections.length === 1) {
         const selection = this.data.selected;
         this.multiRowUpdateForm = this.fb.group({
            chkNecLec: [false],
            chkToolAffected: [false],
            chkCertDataAffected: [false],
            chkTlLeadRelLvl: [false],
            chkDtShippedToDataBank: [false],
            chkNecCompleteDate: [false],
            chkDesignSourceId: [false],
            designSourceId: [selection.designSourceId],
            dtShippedToDatabank: [selection.dtShippedToDatabank],
            necCompleteDate: [selection.necCompleteDate],
            necLec: [selection.necLec, [Validators.pattern(this.NEC_LEC_REGEX)]],
            tlLeadRelLvl: [selection.tlLeadRelLvl],
            certDataAffected: [selection.certDataAffected],
            toolAffected: [selection.toolAffected]
         });
      }
      else {
         this.multiSelect = true;
      }

      const isAllNecLecSame = selections.every(el => {
         return (el.necLec === selections[0].necLec);
      });

      if (isAllNecLecSame) {
         this.isAllNecLecSame = true;
         ctrl.necLec.setValue(selections[0].necLec);
      }

      const isAllToolAffectedSame = selections.every(el => {
         return (el.toolAffected === selections[0].toolAffected);
      });

      if (isAllToolAffectedSame) {
         this.isAllToolAffectedSame = true;
         ctrl.toolAffected.setValue(selections[0].toolAffected);
      }

      const isAllCertDataAffectedSame = selections.every(el => {
         return (el.certDataAffected === selections[0].certDataAffected);
      });

      if (isAllCertDataAffectedSame) {
         this.isAllCertDataAffectedSame = true;
         ctrl.certDataAffected.setValue(selections[0].certDataAffected);
      }

      const isAllTlLeadRelLvlSame = selections.every(el => {
         return (el.tlLeadRelLvl === selections[0].tlLeadRelLvl);
      });

      if (isAllTlLeadRelLvlSame) {
         this.isAllTlLeadRelLvlSame = true;
         ctrl.tlLeadRelLvl.setValue(selections[0].tlLeadRelLvl);
      }

      const isAllDtShippedToDatabankSame = selections.every(el => {
         return (el.fmtdtShippedToDatabank === selections[0].fmtdtShippedToDatabank);
      });

      if (isAllDtShippedToDatabankSame) {
         this.isAllDtShippedToDatabankSame = true;
         ctrl.dtShippedToDatabank.setValue(selections[0].dtShippedToDatabank);
      }

      const isAllNecCompleteDateSame = selections.every(el => {
         return (el.fmtNecCompleteDate === selections[0].fmtNecCompleteDate);
      });

      if (isAllNecCompleteDateSame) {
         this.isAllNecCompleteDateSame = true;
         ctrl.necCompleteDate.setValue(selections[0].necCompleteDate);
      }

      const isAllDesignSourceIdSame = selections.every(el => {
         return (el.designSourceId === selections[0].designSourceId);
      });

      if (isAllDesignSourceIdSame) {
         this.isAllDesignSourceIdSame = true;
         ctrl.designSourceId.setValue(selections[0].designSourceId);
      }

      const selectionsHasAllNecLec = selections.every(el => {
         return (el.necLec);
      });

      if (selectionsHasAllNecLec) {
         this.selectionsHasAllNecLec = true;
      }

      const selectionsHasAllTlLeadRelLvl = selections.every(el => {
         return (el.tlLeadRelLvl);
      });

      if (selectionsHasAllTlLeadRelLvl) {
         this.selectionsHasAllTlLeadRelLvl = true;
      }
      const selectionsHasAllDesignSourceId = selections.every(el => {
         return (el.designSourceId);
      });

      if (selectionsHasAllDesignSourceId) {
         this.selectionsHasAllDesignSourceId = true;
      }

      const selectionsHasAllDtShippedToDatabank = selections.every(el => {
         return (el.dtShippedToDatabank);
      });

      if (selectionsHasAllDtShippedToDatabank) {
         this.selectionsHasAllDtShippedToDatabank = true;
      }

      this.selectionsHasOneDtShippedToDatabank = selections.some(el => {
         return (el.dtShippedToDatabank);
      });

      const selectionsHasNoDtShippedToDatabank = selections.every(el => {
         return (!el.dtShippedToDatabank);
      });

      if (selectionsHasNoDtShippedToDatabank) {
         this.selectionsHasNoDtShippedToDatabank = true;
      }

      const selectionsHasAllNecCompleteDate = selections.every(el => {
         return (el.necCompleteDate);
      });

      if (!selectionsHasAllTlLeadRelLvl || !selectionsHasAllNecLec || !selectionsHasAllDesignSourceId) {
         this.multiRowUpdateForm.get('necLec').enable();
         this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('certDataAffected').enable();
         this.multiRowUpdateForm.get('toolAffected').enable();
         this.multiRowUpdateForm.get('chkNecLec').enable();
         this.multiRowUpdateForm.get('chkToolAffected').enable();
         this.multiRowUpdateForm.get('chkCertDataAffected').enable();
         this.multiRowUpdateForm.get('chkTlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('dtShippedToDatabank').disable();
         this.multiRowUpdateForm.get('chkDtShippedToDataBank').disable();
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
      }

      if (!selectionsHasAllDtShippedToDatabank) {
         this.multiRowUpdateForm.get('necLec').enable();
         this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('certDataAffected').enable();
         this.multiRowUpdateForm.get('toolAffected').enable();
         this.multiRowUpdateForm.get('chkNecLec').enable();
         this.multiRowUpdateForm.get('chkToolAffected').enable();
         this.multiRowUpdateForm.get('chkCertDataAffected').enable();
         this.multiRowUpdateForm.get('chkTlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
      }


      if (this.selectionsHasAllDtShippedToDatabank && (this.isAllDtShippedToDatabankSame || this.isAllNecCompleteDateSame)) {
         this.multiRowUpdateForm.get('necCompleteDate').enable();
         this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
      }
   }

   ngOnDestroy() {
      this._subs.forEach(sub => sub.unsubscribe());
   }


   disableEnableFormField(necComplDate: any, event: MatDatepickerInputEvent<Date>) {
      if (this.multiSelect) {
         this.disableEnableFormFieldIfMultiSelect(necComplDate, event);
      }
      else {
         this.disableEnableFormFieldIfNotMultiSelect(necComplDate, event);
      }
   }

   disableEnableFormFieldIfMultiSelect(necComplDate: any, event: MatDatepickerInputEvent<Date>) {
      if (event.value) {
         if (this.selectionsHasNoDtShippedToDatabank) {
            this.multiRowUpdateForm.get('necLec').enable();
            this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
            this.multiRowUpdateForm.get('certDataAffected').enable();
            this.multiRowUpdateForm.get('toolAffected').enable();
            this.multiRowUpdateForm.get('chkNecLec').enable();
            this.multiRowUpdateForm.get('chkToolAffected').enable();
            this.multiRowUpdateForm.get('chkCertDataAffected').enable();
            this.multiRowUpdateForm.get('chkTlLeadRelLvl').enable();
            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         }
         else {

            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
            this.multiRowUpdateForm.get('designSourceId').disable();
            this.multiRowUpdateForm.controls.chkDesignSourceId.setValue(false);
            this.multiRowUpdateForm.get('chkDesignSourceId').disable();
         }
      }
      else if (!event.value && necComplDate.value) {
         const ref = this.cgpAlertDialogService.showAlertDialog({
            message: 'Removing the Shipped to Databank Date will also remove the NEC Complete Date',
            alert: cgpAlertTypes.danger,
            closeLabel: 'OK'
         });
         ref.afterClosed().subscribe(() => {
            if (this.multiSelect) {
               this.multiRowUpdateForm.get('necCompleteDate').disable();
               this.multiRowUpdateForm.get('necCompleteDate').reset();
               this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
            }
            else {
               this.multiRowUpdateForm.get('necCompleteDate').disable();
               this.multiRowUpdateForm.get('necCompleteDate').reset();
               this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
               this.multiRowUpdateForm.get('necLec').enable();
               this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
               this.multiRowUpdateForm.get('certDataAffected').enable();
               this.multiRowUpdateForm.get('toolAffected').enable();
            }
         });

      }
      else {
         this.multiRowUpdateForm.get('necLec').enable();
         this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('certDataAffected').enable();
         this.multiRowUpdateForm.get('toolAffected').enable();
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('necCompleteDate').reset();
         this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
         this.multiRowUpdateForm.get('designSourceId').enable();
      }
   }

   disableEnableFormFieldIfNotMultiSelect(necComplDate: any, event: MatDatepickerInputEvent<Date>) {
      if (event.value) {
         if (this.selectionsHasNoDtShippedToDatabank) {
            this.multiRowUpdateForm.get('necLec').enable();
            this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
            this.multiRowUpdateForm.get('certDataAffected').enable();
            this.multiRowUpdateForm.get('toolAffected').enable();
            this.multiRowUpdateForm.get('chkNecLec').enable();
            this.multiRowUpdateForm.get('chkToolAffected').enable();
            this.multiRowUpdateForm.get('chkCertDataAffected').enable();
            this.multiRowUpdateForm.get('chkTlLeadRelLvl').enable();
            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         }
         else {
            this.disableFieldsandCheckbox();
            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
            this.multiRowUpdateForm.get('designSourceId').disable();
            this.multiRowUpdateForm.controls.chkDesignSourceId.setValue(false);
            this.multiRowUpdateForm.get('chkDesignSourceId').disable();
         }
      }
      else if (!event.value && necComplDate.value) {
         const ref = this.cgpAlertDialogService.showAlertDialog({
            message: 'Removing the Shipped to Databank Date will also remove the NEC Complete Date',
            alert: cgpAlertTypes.danger,
            closeLabel: 'OK'
         });
         ref.afterClosed().subscribe(() => {
            this.multiRowUpdateForm.get('necCompleteDate').disable();
            this.multiRowUpdateForm.get('necCompleteDate').reset();
            this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
            this.multiRowUpdateForm.get('necLec').enable();
            this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
            this.multiRowUpdateForm.get('certDataAffected').enable();
            this.multiRowUpdateForm.get('toolAffected').enable();
         });

      }
      else {
         this.multiRowUpdateForm.get('necLec').enable();
         this.multiRowUpdateForm.get('tlLeadRelLvl').enable();
         this.multiRowUpdateForm.get('certDataAffected').enable();
         this.multiRowUpdateForm.get('toolAffected').enable();
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('necCompleteDate').reset();
         this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
         this.multiRowUpdateForm.get('designSourceId').enable();
      }
   }
   showAlertShippedToDataBank() {
      if (this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') {
         this.cgpAlertDialogService.showAlertDialog({
            width: '475px',
            message: 'One or more entries is missing required information. Therefore the shipped to databank will be removed.',
            alert: cgpAlertTypes.danger,
            closeLabel: 'OK'
         }).afterClosed().subscribe(
            () => {
               this.multiRowUpdateForm.get('dtShippedToDatabank').reset();
               this.multiRowUpdateForm.controls.chkDtShippedToDataBank.setValue(false);
               this.multiRowUpdateForm.get('necCompleteDate').disable();
               this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
               this.multiRowUpdateForm.get('designSourceId').enable();
               this.multiRowUpdateForm.get('chkDesignSourceId').enable();
               this.multiRowUpdateForm.controls.chkDesignSourceId.setValue(false);
            });
      }
   }

   enableDisableNecCompletionDate(event: MatCheckboxChange) {
      if (!event.checked) {
         if (!this.selectionsHasAllDtShippedToDatabank) {
            this.multiRowUpdateForm.get('necCompleteDate').disable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
         } else {
            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         }
      }
      if (event.checked) {

         if (this.multiRowUpdateForm.get('dtShippedToDatabank').value === null || this.multiRowUpdateForm.get('dtShippedToDatabank').value === '') {
            const ref = this.cgpAlertDialogService.showAlertDialog({
               message: 'Removing the Shipped to Databank Date will also remove the NEC Complete Date',
               alert: cgpAlertTypes.danger,
               closeLabel: 'OK'
            });
            this.multiRowUpdateForm.get('necCompleteDate').disable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         } else {
            this.multiRowUpdateForm.get('necCompleteDate').enable();
            this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         }
      }
   }


   enableDisableNecCompletionDateCheck(event: MatCheckboxChange) {
      if (this.multiSelect) {
         if (!this.selectionsHasNoDtShippedToDatabank) {
            if (event.checked) {
               if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '')
                  && (this.multiRowUpdateForm.get('chkDtShippedToDataBank').value !== true)) {
                  this.cgpAlertDialogService.showAlertDialog({
                     width: '475px',
                     message: 'Select Shipped to Databank Date first before you select NEC Completion Date.',
                     alert: cgpAlertTypes.danger,
                     closeLabel: 'OK'
                  }).afterClosed().subscribe(
                     () => {
                        this.multiRowUpdateForm.controls.chkNecCompleteDate.setValue(false);
                     });
               }
            }
         }
      }

      if (!this.multiSelect) {
         if (event.checked) {
            if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '')
               && (this.multiRowUpdateForm.get('chkDtShippedToDataBank').value !== true)) {
               this.cgpAlertDialogService.showAlertDialog({
                  width: '475px',
                  message: 'Select Shipped to Databank Date first before you select NEC Completion Date.',
                  alert: cgpAlertTypes.danger,
                  closeLabel: 'OK'
               }).afterClosed().subscribe(
                  () => {
                     this.multiRowUpdateForm.controls.chkNecCompleteDate.setValue(false);
                  });
            }
         }
      }
   }

   disableFieldsandCheckbox() {
      this.formFieldDisabled = true;
      this.multiRowUpdateForm.get('necLec').disable();
      this.multiRowUpdateForm.get('tlLeadRelLvl').disable();
      this.multiRowUpdateForm.get('certDataAffected').disable();
      this.multiRowUpdateForm.get('toolAffected').disable();
      this.multiRowUpdateForm.controls.chkNecLec.setValue(false);
      this.multiRowUpdateForm.controls.chkToolAffected.setValue(false);
      this.multiRowUpdateForm.controls.chkCertDataAffected.setValue(false);
      this.multiRowUpdateForm.controls.chkTlLeadRelLvl.setValue(false);
      this.multiRowUpdateForm.get('chkNecLec').disable();
      this.multiRowUpdateForm.get('chkToolAffected').disable();
      this.multiRowUpdateForm.get('chkCertDataAffected').disable();
      this.multiRowUpdateForm.get('chkTlLeadRelLvl').disable();
      this.multiRowUpdateForm.get('dtShippedToDatabank').enable();
      this.multiRowUpdateForm.get('chkDtShippedToDataBank').enable();
   }

   showAlert() {
      this.cgpAlertDialogService.showAlertDialog({
         width: '475px',
         message: 'Clear the Date to Shipped Bank before updating NEC LEC, Is Tool Affected?, Is certification Data Affected? and Tool Lead Release Level.',
         alert: cgpAlertTypes.danger,
         closeLabel: 'OK'
      }).afterClosed().subscribe(
         () => {
            this.disableFieldsandCheckbox();
            this.multiRowUpdateForm.get('designSourceId').enable();
            this.multiRowUpdateForm.controls.chkDesignSourceId.setValue(false);

         }
      );
   }

   enableDisableNeclecTlAfctCertDataTlLead(event: MatCheckboxChange) {
      if (event.checked) {
         this.toolLdReleaseLvlFlag = true;
         if (!this.selectionsHasNoDtShippedToDatabank) {
            if (this.multiSelect) {
               this.showAlert();
            }
         }
      }
   }

   enableDisableShippedtoDateBank(event: any) {
      if ((this.multiRowUpdateForm.get('tlLeadRelLvl').value !== null && this.multiRowUpdateForm.get('tlLeadRelLvl').value !== '')
         && (this.multiRowUpdateForm.get('designSourceId').value !== null && this.multiRowUpdateForm.get('designSourceId').value !== '')
         && (this.multiRowUpdateForm.get('necLec').value !== null && this.multiRowUpdateForm.get('necLec').value !== '')) {
         this.multiRowUpdateForm.get('dtShippedToDatabank').enable();
         this.multiRowUpdateForm.get('chkDtShippedToDataBank').enable();
      } else {
         this.multiRowUpdateForm.get('dtShippedToDatabank').reset();
         this.multiRowUpdateForm.controls.chkDtShippedToDataBank.setValue(false);
         this.multiRowUpdateForm.get('dtShippedToDatabank').disable();
         this.multiRowUpdateForm.get('chkDtShippedToDataBank').disable();
         this.multiRowUpdateForm.get('necCompleteDate').reset();
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('chkNecCompleteDate').disable();
      }
   }


   /**
    * Returns true if any of the enabled checkboxes is checked.
    * This flag controls the Update button.
    *
    * @readonly
    * @type {boolean}
    * @memberof EtcMultiRowUpdateComponent
    */
   get hasEnabledFields(): boolean {
      const formData = this.multiRowUpdateForm.value;
      return formData.chkNecLec ||
         formData.chkToolAffected ||
         formData.chkCertDataAffected ||
         formData.chkTlLeadRelLvl ||
         formData.chkDtShippedToDataBank ||
         formData.chkNecCompleteDate ||
         formData.chkDesignSourceId;
   }

   get hasAllSameData(): boolean {
      if (this.isAllNecLecSame && this.isAllToolAffectedSame && this.isAllCertDataAffectedSame
         && this.isAllTlLeadRelLvlSame && this.isAllDtShippedToDatabankSame
         && this.isAllNecCompleteDateSame && this.isAllDesignSourceIdSame) {
         return true;
      }
   }

   get ischkNecLecChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkNecLec').value) {
         return true;
      }
   }

   get isChkToolAffectedChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkToolAffected').value) {
         return true;
      }
   }

   get isChkCertDataAffectedChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkCertDataAffected').value) {
         return true;
      }
   }

   get isChkTlLeadRelLvlChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkTlLeadRelLvl').value) {
         return true;
      }
   }

   get isChkDtShippedToDataBankChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkDtShippedToDataBank').value) {
         return true;
      }
   }
   get isChkNecCompleteDateChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkNecCompleteDate').value) {
         return true;
      } else {
         return false;
      }
   }
   get isChkDesignSourceIdChecked(): boolean {
      if (this.multiRowUpdateForm.get('chkDesignSourceId').value) {
         return true;
      }
   }

   get isAnyFieldMissingData(): boolean {
      if (!this.selectionsHasAllNecLec || !this.selectionsHasAllTlLeadRelLvl || !this.selectionsHasAllDesignSourceId) {
         return true;
      }
   }

   onUpdateClick() {
      this.conditionCheck();
      if (this.selectionsHasOneDtShippedToDatabank &&
         (this.multiRowUpdateForm.get('dtShippedToDatabank').value === null || this.multiRowUpdateForm.get('dtShippedToDatabank').value === '') && this.isChkDtShippedToDataBankChecked) {
         this.multiRowUpdateForm.get('necCompleteDate').disable();
         this.multiRowUpdateForm.get('chkNecCompleteDate').enable();
         const ref = this.cgpAlertDialogService.showAlertDialog({
            message: 'Removing the Shipped to Databank Date will also remove the NEC Complete Date',
            alert: cgpAlertTypes.danger,
            closeLabel: 'OK',
            onClosed: () => {
               this.update();
            }
         });

      } else {
         this.update();
      }
   }

   update() {
      if (!this.checkCondition) {
         this.loading = true;
         const body = this.createBody();
         this.ecmToolChangeService.multiEditToolChange(body).subscribe({
            next: () => {
               this.cgpAlertDialogService.showAlertDialog({
                  title: 'Update Complete',
                  message: 'Changes successful!',
                  alert: cgpAlertTypes.success,
                  closeLabel: 'Close'
               }).afterClosed().subscribe(() => this.dialogRef.close({
                  reload: true,
                  isMultiedit: true,
               }));
            },
            error: () => {
               this.cgpAlertDialogService.showAlertDialog({
                  title: 'Update Failed',
                  message: 'Changes unsuccessful! Please try again.',
                  alert: cgpAlertTypes.danger,
                  closeLabel: 'Close'
               }).afterClosed().subscribe({ complete: () => this.loading = false });
            }
         });
      }

   }

   onCancelClick() {
      this.dialogRef.close();
   }

   selectAll() {
      const ctrl = this.multiRowUpdateForm.controls;
      ctrl.chkNecLec.setValue(true);
      ctrl.chkToolAffected.setValue(true);
      ctrl.chkCertDataAffected.setValue(true);
      ctrl.chkTlLeadRelLvl.setValue(true);
      ctrl.chkDtShippedToDataBank.setValue(true);
      ctrl.chkNecCompleteDate.setValue(true);
      ctrl.chkDesignSourceId.setValue(true);

      if (this.multiSelect) {
         if (!this.hasAllSameData) {
            if (!this.selectionsHasNoDtShippedToDatabank) {
               this.showAlert();
            }
         }
      }
   }


   selectNone() {
      const ctrl = this.multiRowUpdateForm.controls;
      ctrl.chkNecLec.setValue(false);
      ctrl.chkToolAffected.setValue(false);
      ctrl.chkCertDataAffected.setValue(false);
      ctrl.chkTlLeadRelLvl.setValue(false);
      ctrl.chkDtShippedToDataBank.setValue(false);
      ctrl.chkNecCompleteDate.setValue(false);
      ctrl.chkDesignSourceId.setValue(false);
   }


   conditionCheck() {
      if (!this.multiSelect || (this.multiSelect && this.hasAllSameData)) {
         if (this.selectionsHasAllNecLec && this.selectionsHasAllTlLeadRelLvl && this.selectionsHasAllDesignSourceId) {
            if ((((this.multiRowUpdateForm.get('tlLeadRelLvl').value === null || this.multiRowUpdateForm.get('tlLeadRelLvl').value === '') && this.isChkTlLeadRelLvlChecked)
               || ((this.multiRowUpdateForm.get('designSourceId').value === null || this.multiRowUpdateForm.get('designSourceId').value === '') && this.isChkDesignSourceIdChecked)
               || ((this.multiRowUpdateForm.get('necLec').value === null || this.multiRowUpdateForm.get('necLec').value === '')) && this.ischkNecLecChecked)
               && ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') && this.isChkDtShippedToDataBankChecked)) {
               this.checkCondition = true;
               this.cgpAlertDialogService.showAlertDialog({
                  width: '475px',
                  message: 'Clear the Date to Shipped Bank before updating NEC LEC, Is Tool Affected?, Is certification Data Affected? and Tool Lead Release Level.',
                  alert: cgpAlertTypes.danger,
                  closeLabel: 'OK'
               });
            }
            else {
               this.checkCondition = false;
            }
         } else {
            if (this.isFieldEmpty()) {
               this.checkCondition = true;
               this.cgpAlertDialogService.showAlertDialog({
                  width: '475px',
                  message: 'NEC LEC, Tool Lead Release Level and Design Source are required before entering the Date to Shipped Bank.',
                  alert: cgpAlertTypes.danger,
                  closeLabel: 'OK'
               });
            }
            else {
               this.checkCondition = false;
            }
         }
      } else if (this.multiSelect && this.isAnyFieldMissingData) {
         if ((((this.multiRowUpdateForm.get('tlLeadRelLvl').value !== null || this.multiRowUpdateForm.get('tlLeadRelLvl').value !== '') && this.isChkTlLeadRelLvlChecked)
            && ((this.multiRowUpdateForm.get('designSourceId').value !== null || this.multiRowUpdateForm.get('designSourceId').value !== '') && this.isChkDesignSourceIdChecked)
            && ((this.multiRowUpdateForm.get('necLec').value !== null || this.multiRowUpdateForm.get('necLec').value !== '')) && this.ischkNecLecChecked)
            && ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') && this.isChkDtShippedToDataBankChecked)) {
            this.checkCondition = false;
         }
         else if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') && this.isChkDtShippedToDataBankChecked) {
            this.checkCondition = true;
            this.showAlertShippedToDataBank();
         } else if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value === null || this.multiRowUpdateForm.get('dtShippedToDatabank').value === '') && this.isChkDtShippedToDataBankChecked) {
            this.checkCondition = false;
         }
         else {
            this.checkCondition = false;
         }

      } else if (this.multiSelect) {
         if ((((this.multiRowUpdateForm.get('tlLeadRelLvl').value === null || this.multiRowUpdateForm.get('tlLeadRelLvl').value === '') && this.isChkTlLeadRelLvlChecked)
            || ((this.multiRowUpdateForm.get('designSourceId').value === null || this.multiRowUpdateForm.get('designSourceId').value === '') && this.isChkDesignSourceIdChecked)
            || ((this.multiRowUpdateForm.get('necLec').value === null || this.multiRowUpdateForm.get('necLec').value === '')) && this.ischkNecLecChecked)) {

            if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') && this.isChkDtShippedToDataBankChecked) {
               this.checkCondition = true;
               this.cgpAlertDialogService.showAlertDialog({
                  width: '475px',
                  message: 'NEC LEC, Tool Lead Release Level and Design Source are required before entering the Date to Shipped Bank.',
                  alert: cgpAlertTypes.danger,
                  closeLabel: 'OK'
               });
            }
            else if ((this.multiRowUpdateForm.get('dtShippedToDatabank').value === null || this.multiRowUpdateForm.get('dtShippedToDatabank').value === '') && !this.isChkDtShippedToDataBankChecked) {
               this.checkCondition = true;
               this.showAlert();
            }

         } else if (((this.multiRowUpdateForm.get('dtShippedToDatabank').value === null || this.multiRowUpdateForm.get('dtShippedToDatabank').value === '') && this.isChkDtShippedToDataBankChecked)
            && (((this.multiRowUpdateForm.get('necCompleteDate').value === null || this.multiRowUpdateForm.get('necCompleteDate').value === '') && !this.isChkNecCompleteDateChecked)
               || ((this.multiRowUpdateForm.get('necCompleteDate').value !== null || this.multiRowUpdateForm.get('necCompleteDate').value !== '') && !this.isChkNecCompleteDateChecked))) {
            this.checkCondition = false;
         } else {
            this.checkCondition = false;
         }

      }

   }

   isFieldEmpty() {
      if (((this.multiRowUpdateForm.get('dtShippedToDatabank').value !== null && this.multiRowUpdateForm.get('dtShippedToDatabank').value !== '') && this.isChkDtShippedToDataBankChecked)
         && (((!this.selectionsHasAllTlLeadRelLvl || !this.isAllTlLeadRelLvlSame)
            && ((this.multiRowUpdateForm.get('tlLeadRelLvl').value !== null || this.multiRowUpdateForm.get('tlLeadRelLvl').value !== '') && !this.isChkTlLeadRelLvlChecked))
            || ((!this.selectionsHasAllNecLec || !this.isAllNecLecSame)
               && ((this.multiRowUpdateForm.get('necLec').value !== null || this.multiRowUpdateForm.get('necLec').value !== '') && !this.ischkNecLecChecked))
            || ((!this.selectionsHasAllDesignSourceId || !this.isAllDesignSourceIdSame)
               && ((this.multiRowUpdateForm.get('designSourceId').value !== null || this.multiRowUpdateForm.get('designSourceId').value !== '') && !this.isChkDesignSourceIdChecked)))
      ) {
         return true;
      }
      return false;
   }

   private convertData(formData, body) {
      Object.keys(formData).forEach(
         (el) => {
            if (typeof formData[el] === 'boolean') {
               body[el] = (formData[el]) ? 1 : 0;
            } else {
               body[el] = formData[el];
            }
         }
      );
   }

   private createBody() {
      const designSource = this.designSources.find(el => el.id === this.multiRowUpdateForm.value.designSourceId);
      const body = {};
      const arr = [];
      const formData = this.multiRowUpdateForm.value;
      const toolChangeIds = this.data.selections.map(el => el.toolChangeId);
      // Iterate through the formData and:
      // convert boolean values into expected number values
      // convert date values into expected date ISO string value

      this.convertData(formData, body);

      body['sourceName'] = (designSource) ? designSource.sourceName : '';
      body['necLec'] =
         (
            body['necLec'] === 'REL' || body['necLec'] === '(REL' || body['necLec'] === 'REL)' ||
            body['necLec'] === 'rel' || body['necLec'] === '(rel' || body['necLec'] === 'rel)'
         ) ? '(REL)'
            : body['necLec'] ? body['necLec'].toString().toUpperCase() : body['necLec'];
      body['tlLeadRelLvl'] = body['tlLeadRelLvl'] ? body['tlLeadRelLvl'].toString().toUpperCase() : body['tlLeadRelLvl'];

      // Append the toolChangeIds from the tool change selection before
      // opening the multi-row edit dialog
      toolChangeIds.forEach(id => {
         if (id) {
            arr.push({
               ...body,
               toolChangeId: id
            });
         }
      });
      return arr;
   }
}